/**
 * Browser-safe copy of `@valvur/shared-grpc` VALVUR_FEATURE_FLAG_CATALOG.
 * Do not import the catalog from shared-grpc in Angular — it pulls server deps.
 */
export const VALVUR_FEATURE_FLAG_CATALOG = [
  {
    code: 'GOLD_SKIP_PARENT_BALANCE_UPDATES',
    key: 'gold.skip_parent_balance_updates',
    description:
      'When enabled, skips parent gold wallet balance changes for duty reward reserve/settle and cashout confirm (ledger entries may still be written with zero deltas).',
    defaultEnabled: false,
  },
  {
    code: 'GOLD_AUTO_COMPLETE_CASHOUT_ON_REQUEST',
    key: 'gold.auto_complete_cashout_on_request',
    description:
      'When enabled, RequestCashout runs parent approval and kid confirmation in one transaction (until fiscal/banking provider is integrated). Disable to use separate ApproveCashoutRequest and ConfirmCashoutReceived steps.',
    defaultEnabled: true,
  },
  {
    code: 'CHALLENGE_USE_TIME_SPENT_SHIELD_MULTIPLIER',
    key: 'challenge.use_time_spent_shield_multiplier',
    description:
      'When enabled, task shield rewards apply a time-on-task factor vs estimatedTime (±15% faster / −25% slower).',
    defaultEnabled: false,
  },
  {
    code: 'PATROL_DISCORD',
    key: 'patrol.discord',
    description:
      'When enabled, Discord Patrol partner APIs and ingress are available via api-gateway.',
    defaultEnabled: false,
  },
  {
    code: 'PATROL_ROBLOX',
    key: 'patrol.roblox',
    description:
      'When enabled, Roblox Patrol partner APIs and game-server ingress are available via api-gateway.',
    defaultEnabled: false,
  },
  {
    code: 'IDENTITY_LAYER',
    key: 'identity.layer',
    description:
      'Master switch for the Valvur identity layer: verification tiers, KYC orchestration, and identity policy evaluation.',
    defaultEnabled: false,
  },
  {
    code: 'IDENTITY_FULLY_VERIFIED_REQUIRES_COURSE',
    key: 'identity.fully_verified.requires_course',
    description:
      'When enabled (with identity.layer on), FULLY_VERIFIED requires completion of all published courses flagged identity_mandatory in cs_course.',
    defaultEnabled: false,
  },
  {
    code: 'DUTIES_FAMILY',
    key: 'duties.family',
    description:
      'When enabled, parents can create family (co-participation) duties and mark their portion complete. Disable until kid app supports shared duties.',
    defaultEnabled: false,
  },
  {
    code: 'FAMILY_MEMBERSHIP',
    key: 'family.membership',
    description:
      'When enabled, Family household membership is active. Enabling (off→on) runs BackfillFamilies for existing parents.',
    defaultEnabled: false,
  },
  {
    code: 'FAMILY_SHARED_XP_POOL',
    key: 'family.shared_xp_pool',
    description:
      'When enabled, XP pool is household-scoped (pas_experience_pool.family_id). Enabling (off→on) runs BackfillFamilyExperiencePools. Requires family.membership.',
    defaultEnabled: false,
  },
  {
    code: 'PAYMENTS_ENABLED',
    key: 'payments.enabled',
    description:
      'Master switch for household billing UX and entitlement cuts. Off→on should backfill unpaid Families to Base (cutover job). Checkout/webhook side-effects when on.',
    defaultEnabled: false,
  },
  {
    code: 'PAYMENTS_ENFORCE_QUOTAS',
    key: 'payments.enforce_quotas',
    description:
      'When enabled (with payments.enabled), AI/duty/XP quotas are hard-enforced. When off, meters may still be visible.',
    defaultEnabled: false,
  },
  {
    code: 'PAYMENTS_ADDONS',
    key: 'payments.addons',
    description:
      'When enabled, XP/AI one-time pack checkouts are available.',
    defaultEnabled: false,
  },
] as const;

export type ValvurFeatureFlagKey =
  (typeof VALVUR_FEATURE_FLAG_CATALOG)[number]['key'];

export function featureFlagCatalogEntry(
  key: string,
): (typeof VALVUR_FEATURE_FLAG_CATALOG)[number] | undefined {
  return VALVUR_FEATURE_FLAG_CATALOG.find((entry) => entry.key === key);
}
